// Sequential video loader using lodash throttle
import throttle from 'lodash/throttle'
import { attachHlsIfNeeded } from './hls_loader'

// Queue for videos waiting to be loaded
let videoLoadQueue = []
// Flag to track if we're currently processing the queue
let isProcessingQueue = false
// Remember sources that reached loadeddata during this page session. AJAX
// navigation replaces the overview markup with fresh <video> elements, but
// revisited sources are already in the browser's media cache. Let those skip
// the one-video-per-second queue so thumbnail videos return promptly.
const loadedVideoSources = new Set()

const getVideoSourceUrls = function (video) {
	return Array.from(video.querySelectorAll('source'))
		.map(function (source) {
			return source.dataset.src || source.getAttribute('src') || ''
		})
		.filter(Boolean)
}

const hasPreviouslyLoadedSource = function (video) {
	return getVideoSourceUrls(video).some(function (url) {
		return loadedVideoSources.has(url)
	})
}

const rememberLoadedSources = function (video) {
	getVideoSourceUrls(video).forEach(function (url) {
		loadedVideoSources.add(url)
	})
}

const loadVideo = function (video) {
	if (!video._layRememberLoadedSourcesBound) {
		video._layRememberLoadedSourcesBound = true
		video.addEventListener('loadeddata', function () {
			rememberLoadedSources(video)
		}, { once: true })
	}

	for (var source in video.children) {
		var videoSource = video.children[source]
		if (typeof videoSource.tagName === 'string' && videoSource.tagName === 'SOURCE' && videoSource.dataset.src != undefined) {
			videoSource.src = videoSource.dataset.src
		}
	}

	video.setAttribute('preload', 'auto')
	try {
		video.load()
	} catch (e) {}

	video.classList.remove('video-lazyload')
	video.classList.add('loaded')
	if (video.classList.contains('autoplay')) {
		video.setAttribute('autoplay', '')
	}

	attachHlsIfNeeded(video)
}

// Throttled function to process a single video from the queue
const processNextVideoInQueue = throttle(function () {
	// Navigating away can detach videos that were waiting in the global queue.
	// Drop them before choosing the next item, otherwise a returning overview
	// waits behind media elements that can no longer be shown.
	videoLoadQueue = videoLoadQueue.filter(function (video) {
		return video && video.isConnected && video.classList.contains('video-lazyload')
	})

	if (videoLoadQueue.length === 0) {
		isProcessingQueue = false
		return
	}

	isProcessingQueue = true
	const video = videoLoadQueue.shift()

	loadVideo(video)

	// Process next video after this one is done
	processNextVideoInQueue()
}, 1000) // 1 second throttle

// Add video to queue and start processing if not already
function queueVideoForLoading(video) {
	if (!video || !video.isConnected || !video.classList.contains('video-lazyload')) {
		return
	}
	if (hasPreviouslyLoadedSource(video)) {
		loadVideo(video)
		return
	}
	if (videoLoadQueue.includes(video)) {
		return
	}
	videoLoadQueue.push(video)
	if (!isProcessingQueue) {
		processNextVideoInQueue()
	}
}

var loadVideosInstantly = function ($target) {
	$target.find('video.video-lazyload').each(function () {
		loadVideo(this)
	})
}

var loadSpecificVideo = function (video) {
	if (video.classList.contains('video-lazyload')) {
		queueVideoForLoading(video)
	}
}

function LazyLoadVideos($target) {
	var $target = $target

	var init = function () {
		var $lazyVideos = $target.find('video.video-lazyload')

		if ('IntersectionObserver' in window) {
			var lazyVideoObserver = new IntersectionObserver(function (entries, observer) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) {
						queueVideoForLoading(entry.target)
						lazyVideoObserver.unobserve(entry.target)
					}
				})
			}, {
				rootMargin: '100%' // Start loading one viewport height away
			})

			$lazyVideos.each(function () {
				lazyVideoObserver.observe(this)
			})
		} else {
			loadVideosInstantly($target)
		}
	}

	return {
		init: init,
	}
}

var observeSpecificVideo = function (video) {
	if (!video || !video.classList.contains('video-lazyload')) {
		return
	}
	if ('IntersectionObserver' in window) {
		var observer = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (entry.isIntersecting) {
					queueVideoForLoading(entry.target)
					observer.unobserve(entry.target)
				}
			})
		}, {
			rootMargin: '100%'
		})
		observer.observe(video)
	} else {
		loadVideo(video)
	}
}

var shouldLazyLoadVideos = function () {
	// Missing means an older cached/localized payload: keep the historical
	// global-setting behavior. New payloads use explicit strings because
	// wp_localize_script serializes PHP booleans as "1" / "".
	var browserAllowsLazyLoading = typeof layData.videoLazyLoadingAllowedForCurrentBrowser === 'undefined' || layData.videoLazyLoadingAllowedForCurrentBrowser == 'on'
	return layData.videoLazyLoading == 'on' && browserAllowsLazyLoading
}

var initLazyLoadVideosOnScroll = function ($target) {
	var loadVideosObject = new LazyLoadVideos($target)
	loadVideosObject.init()
}

var load_html5_videos_controller = {
	loadVideosInstantly: function ($target) {
		loadVideosInstantly($target)
	},
	initLazyLoadVideosOnScroll: function ($target) {
		initLazyLoadVideosOnScroll($target)
	},
	loadSpecificVideo: function ($video) {
		loadSpecificVideo($video)
	},
	observeSpecificVideo: function (video) {
		observeSpecificVideo(video)
	},
	shouldLazyLoadVideos: function () {
		return shouldLazyLoadVideos()
	},
}

export default load_html5_videos_controller
